import React from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Skeleton } from 'antd';
import DrawerHeader from '@/components/atoms/DrawerHeader';
import { LogisticsFeedbackItem } from '@/types/logisticsFeedback';
import { FeedbackMilestonesList } from './FeedbackMilestonesList';
import { FeedbackProductsList } from './FeedbackProductsList';
import { FeedbackFullBudgetReview } from './FeedbackFullBudgetReview';
import { FeedbackOverallReview } from './FeedbackOverallReview';
import { FeedbackOrderDetails } from './FeedbackOrderDetails';
import { FeedbackPartiesCards } from './FeedbackPartiesCards';
import { FeedbackPaymentSummary } from './FeedbackPaymentSummary';
import { FeedbackLocationMap } from './FeedbackLocationMap';
import { formatOrderDate, formatOrderTime } from '../utils/formatFeedbackDate';

interface FeedbackDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  feedbackItem: LogisticsFeedbackItem | null;
  loading?: boolean;
  onNext?: () => void;
  onPrev?: () => void;
  hasNext?: boolean;
  hasPrev?: boolean;
  'data-testid'?: string;
}

export const FeedbackDetailsDrawer: React.FC<FeedbackDetailsDrawerProps> = ({
  open,
  onClose,
  feedbackItem,
  loading = false,
  onNext,
  onPrev,
  hasNext = false,
  hasPrev = false,
  'data-testid': dataTestId = 'feedback-details-drawer',
}) => {
  const { t, i18n } = useTranslation();

  if (!feedbackItem && !open) return null;

  const isMilestoneView = Boolean(
    feedbackItem?.hasMilestones &&
    feedbackItem?.milestones &&
    feedbackItem?.milestones.length > 0
  );

  return (
    <Drawer
      title={
        <DrawerHeader
          title={t('logisticsFeedback.feedbackDetails')}
          onClose={onClose}
          onNext={onNext}
          onPrev={onPrev}
          nextDisabled={!hasNext || loading}
          prevDisabled={!hasPrev || loading}
          data-test={`${dataTestId}-header`}
        />
      }
      open={open}
      onClose={onClose}
      width={816}
      destroyOnClose
      closeIcon={null}
      className="[&_.ant-drawer-body]:!p-6 [&_.ant-drawer-body]:!bg-light-surface-primary dark:[&_.ant-drawer-body]:!bg-dark-surface-primary"
      data-testid={dataTestId}
    >
      {loading ? (
        <div className="space-y-6" data-testid={`${dataTestId}-skeleton`}>
          <Skeleton active avatar paragraph={{ rows: 4 }} />
          <Skeleton active paragraph={{ rows: 4 }} />
          <Skeleton active paragraph={{ rows: 3 }} />
        </div>
      ) : feedbackItem ? (
        <div className="space-y-6" data-testid={`${dataTestId}-content`}>
          {/* 1. Main Feedback Breakdown: Milestones vs. Full Budget Criteria vs. Product-level */}
          {isMilestoneView ? (
            <FeedbackMilestonesList
              milestones={feedbackItem.milestones!}
              productsCount={
                feedbackItem.productsCount ??
                (feedbackItem.milestones?.reduce(
                  (sum, m) => sum + m.products.length,
                  0
                ) || 0)
              }
              data-testid={`${dataTestId}-milestones`}
            />
          ) : (
            <>
              <FeedbackProductsList
                products={feedbackItem.products || []}
                productsCount={
                  feedbackItem.productsCount ??
                  (feedbackItem.products?.length || 0)
                }
                data-testid={`${dataTestId}-products`}
              />

              {feedbackItem.fullBudgetReview && (
                <FeedbackFullBudgetReview
                  review={feedbackItem.fullBudgetReview}
                  data-testid={`${dataTestId}-full-budget-review`}
                />
              )}
            </>
          )}

          {/* Overall carrier shipment review */}
          {feedbackItem.overallReview && (
            <FeedbackOverallReview
              totalReviewed={feedbackItem.overallReview.totalReviewed}
              totalProducts={feedbackItem.overallReview.totalProducts}
              shipmentRating={feedbackItem.overallReview.shipmentRating}
              shipmentReason={feedbackItem.overallReview.shipmentReason}
              shipmentNotes={feedbackItem.overallReview.shipmentNotes}
              attachments={feedbackItem.overallReview.attachments}
              data-testid={`${dataTestId}-overall-review`}
            />
          )}

          {/* 2. Order Details */}
          <FeedbackOrderDetails
            orderId={feedbackItem.orderId}
            logisticsCompanyName={feedbackItem.logisticsCompanyName}
            date={formatOrderDate(feedbackItem.createdDate, i18n.language)}
            time={formatOrderTime(feedbackItem.createdDate, i18n.language)}
            data-testid={`${dataTestId}-order-details`}
          />

          {/* 3. Seller Information & Buyer Information */}
          <FeedbackPartiesCards
            seller={{
              name: feedbackItem.sellerName,
              avatar: feedbackItem.sellerAvatar,
              email: feedbackItem.sellerEmail,
              phone: feedbackItem.sellerPhone,
            }}
            buyer={{
              name: feedbackItem.buyerName,
              avatar: feedbackItem.buyerAvatar,
              email: feedbackItem.buyerEmail,
              phone: feedbackItem.buyerPhone,
            }}
            data-testid={`${dataTestId}-parties`}
          />

          {/* 4. Payment summary */}
          {feedbackItem.paymentSummary && (
            <FeedbackPaymentSummary
              payment={feedbackItem.paymentSummary}
              data-testid={`${dataTestId}-payment-summary`}
            />
          )}

          {/* 5. Location map */}
          {feedbackItem.location &&
            Boolean(feedbackItem.location.lat && feedbackItem.location.lng) && (
              <FeedbackLocationMap
                location={feedbackItem.location}
                data-testid={`${dataTestId}-location-map`}
              />
            )}
        </div>
      ) : null}
    </Drawer>
  );
};
